import { StateComponent } from '@highimpact/dashboard-ui';
import { forwardRef, LegacyRef, useMemo } from 'react';
import Plot from '../../../../internal/plot/Plot';
import {
  buildHistogramMeasureColumns,
  resolveElementIntervalNames,
} from '../../../../runtime/buildElementPlotMetadata';
import {
  buildHistogramAxisConfigFromElement,
  buildPlotlyLayoutFromElement,
} from '../../../../runtime/buildPlotlyLayoutFromElement';
import type { MeasureRow } from '../../../../runtime/measureRow.types';
import { getDefaultConfig, PlotProps } from '../../commons/plots/helpers';
import {
  addHistogramSpecificLayoutSettings,
  getDefaultLayout,
} from '../../commons/plots/plotly-operations';
import { useMultiIntervalElementMeasureRows } from '../shared/useMultiIntervalElementMeasureRows';
import { usePlotPngInstanceRegistration } from '../shared/usePlotPngInstanceRegistration';

function readHistogramMeasureValue(
  row: MeasureRow,
  columnName: string,
  columnIndex: number
): number | null {
  const raw = row[columnName] ?? row[String(columnIndex)];
  if (raw === null || raw === undefined) {
    return null;
  }

  const numeric = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

function splitLabel(label: string, maxLen = 50): string {
  if (label.length <= maxLen) return label;

  const midpoint = Math.floor(label.length / 2);
  let splitPos = label.lastIndexOf(' ', midpoint);
  if (splitPos === -1 || splitPos > maxLen) {
    splitPos = maxLen;
  }

  return label.slice(0, splitPos) + '<br>' + label.slice(splitPos + 1);
}

function HistogramPlot(
  { dataset, elementData, dashboardElementId, widget_id }: PlotProps,
  ref: LegacyRef<HTMLDivElement>
) {
  const {
    dataByInterval,
    loading,
    error,
    usesElementMeasureRequest,
    labelMap,
  } = useMultiIntervalElementMeasureRows({
    element: elementData,
  });

  const plotName = elementData.name ?? 'Plot';
  usePlotPngInstanceRegistration({
    dashboardElementId,
    widgetId: widget_id,
    plotName,
    enabled: !(usesElementMeasureRequest && (loading || error)),
  });
  const measureColumns = useMemo(
    () => buildHistogramMeasureColumns(elementData, labelMap),
    [elementData, labelMap]
  );
  const intervalNames = useMemo(
    () => resolveElementIntervalNames(elementData),
    [elementData]
  );
  const axisConfig = useMemo(
    () => buildHistogramAxisConfigFromElement(elementData),
    [elementData]
  );
  const layout = useMemo(
    () =>
      addHistogramSpecificLayoutSettings(
        getDefaultLayout(buildPlotlyLayoutFromElement(elementData))
      ),
    [elementData]
  );

  const legacyIntervals =
    elementData.properties &&
    typeof elementData.properties === 'object' &&
    'intervals' in elementData.properties &&
    Array.isArray(elementData.properties.intervals)
      ? elementData.properties.intervals
      : [];
  const legacyYCols: Array<{ name: string; axis_id?: number }> = [];

  const normalizedDataset =
    usesElementMeasureRequest && dataByInterval
      ? dataByInterval
      : dataset?.data
      ? [dataset.data]
      : [];
  const timeRangeNames = usesElementMeasureRequest
    ? intervalNames
    : legacyIntervals.map((range) => range.name ?? 'Intervalle');

  if (usesElementMeasureRequest && loading) {
    return (
      <div ref={ref} className="widget-content">
        <StateComponent
          name="Veuillez patienter !"
          message="Chargement en cours"
          spinner
        />
      </div>
    );
  }

  if (usesElementMeasureRequest && error) {
    return (
      <div ref={ref} className="widget-content">
        <div>Impossible de charger les données de l'histogramme.</div>
      </div>
    );
  }

  const traces: Plotly.Data[] = timeRangeNames.map((rangeName, range_index) => {
    const range_data = normalizedDataset[range_index] ?? [];
    const colNames: string[] = [];
    const colVals: number[] = [];
    const colValsPrettified: string[] = [];
    const columns = usesElementMeasureRequest
      ? measureColumns.map((column) => ({
          name: column.name,
          unit: axisConfig[0]?.unit ?? '',
        }))
      : legacyYCols.map((col) => ({
          name: col.name,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          unit: axisConfig[col.axis_id!]?.unit ?? '',
        }));

    columns.forEach((column, columnIndex) => {
      const validData = range_data
        .map((data) =>
          readHistogramMeasureValue(data, column.name, columnIndex)
        )
        .filter((value): value is number => value !== null);
      if (validData.length === 0) {
        return;
      }

      colNames.push(splitLabel(column.name));
      const avg = validData.reduce((a, b) => a + b, 0) / validData.length;
      colVals.push(avg);
      colValsPrettified.push(`${avg.toFixed(2)} ${column.unit}`.trim());
    });

    return {
      name: rangeName,
      type: 'bar',
      x: colNames,
      y: colVals,
      customdata: colValsPrettified,
      yaxis: 'y',
      autobinx: false,
      hovertemplate: `%{customdata}`,
    };
  });

  return (
    <div ref={ref} className="widget-content">
      <Plot
        className="plot"
        layout={layout}
        data={traces}
        config={getDefaultConfig(plotName)}
      />
    </div>
  );
}

export default forwardRef(HistogramPlot);
